import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Download, Layers } from "lucide-react";
import { downloadCsv } from "@/lib/download";

const COL = "border-r border-slate-200 last:border-r-0 px-2 py-2 text-[11px] text-slate-800 align-middle whitespace-nowrap";
const HDR = "border-r border-emerald-800 last:border-r-0 px-2 py-2 text-[10px] uppercase tracking-wide text-white font-semibold whitespace-nowrap text-center";

const STATUS_COLORS = {
  Received: "bg-blue-50 text-blue-700 border-blue-200",
  Acknowledged: "bg-amber-50 text-amber-700 border-amber-200",
  "In Production": "bg-violet-50 text-violet-700 border-violet-200",
  Dispatched: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function ConsolidatedView() {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [customer, setCustomer] = useState("all");
  const [poStatus, setPoStatus] = useState("all");
  const [lineStatus, setLineStatus] = useState("all");

  useEffect(() => {
    api.get("/po").then((r) => setPos(r.data)).finally(() => setLoading(false));
  }, []);

  // Flatten into rows and apply filters
  const { rows, customers, stats } = useMemo(() => {
    const custSet = new Set();
    const flat = [];
    for (const po of pos) {
      if (po.customer_name) custSet.add(po.customer_name);
      (po.line_items || []).forEach((li, idx) => {
        flat.push({ po, li, idx });
      });
    }
    const t = q.trim().toLowerCase();
    const filtered = flat.filter(({ po, li }) => {
      if (customer !== "all" && po.customer_name !== customer) return false;
      if (poStatus !== "all" && po.status !== poStatus) return false;
      if (lineStatus !== "all" && (li.line_status || "OPEN") !== lineStatus) return false;
      if (t) {
        const hay = [po.po_number, po.customer_name, li.part_name, li.material_no, li.part_code, li.description, li.hose_spec, li.fitting_a, li.fitting_b, li.remark_1, li.remark_2].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(t)) return false;
      }
      return true;
    });
    const s = {
      lines: filtered.length,
      totalQty: filtered.reduce((n, x) => n + Number(x.li.quantity || 0), 0),
      pendingQty: filtered.reduce((n, x) => n + Number(x.li.pending_qty || 0), 0),
      pendingValue: filtered.reduce((n, x) => n + Number(x.li.pending_qty || 0) * Number(x.li.unit_price || 0), 0),
    };
    return { rows: filtered, customers: Array.from(custSet).sort(), stats: s };
  }, [pos, q, customer, poStatus, lineStatus]);

  return (
    <Layout
      title="All Decoded Lines"
      subtitle="Every decoded line item across every PO, on one page — filter, search, and export the whole thing"
      actions={
        <button onClick={() => downloadCsv("/export/po", "purchase_orders.csv")} data-testid="export-consolidated-btn" className="text-xs text-slate-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
          <Download size={14} /> Export CSV
        </button>
      }
    >
      <div className="bg-white border border-gray-200 rounded-md">
        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 p-4 border-b border-gray-100">
          <div className="relative sm:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              data-testid="consolidated-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search PO#, customer, material, description, hose, fitting..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:outline-none focus:border-slate-400"
            />
          </div>
          <select value={customer} onChange={(e) => setCustomer(e.target.value)} data-testid="filter-customer" className="text-sm border border-gray-200 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-slate-300 focus:outline-none">
            <option value="all">All customers</option>
            {customers.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex gap-2">
            <select value={poStatus} onChange={(e) => setPoStatus(e.target.value)} data-testid="filter-po-status" className="flex-1 text-sm border border-gray-200 rounded-md px-2 py-2 bg-white focus:ring-2 focus:ring-slate-300 focus:outline-none">
              <option value="all">PO: all</option>
              <option value="Received">Received</option>
              <option value="Acknowledged">Acknowledged</option>
              <option value="In Production">In Production</option>
              <option value="Dispatched">Dispatched</option>
            </select>
            <select value={lineStatus} onChange={(e) => setLineStatus(e.target.value)} data-testid="filter-line-status" className="flex-1 text-sm border border-gray-200 rounded-md px-2 py-2 bg-white focus:ring-2 focus:ring-slate-300 focus:outline-none">
              <option value="all">Line: all</option>
              <option value="OPEN">OPEN</option>
              <option value="CLOSED">CLOSED</option>
            </select>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex flex-wrap items-center gap-6 px-4 py-3 border-b border-gray-100 bg-gray-50/50">
          <div className="text-xs">
            <span className="text-gray-500 uppercase tracking-widest font-mono">Lines</span>
            <span className="ml-2 font-mono font-semibold text-slate-900" data-testid="stat-lines">{stats.lines}</span>
          </div>
          <div className="text-xs">
            <span className="text-gray-500 uppercase tracking-widest font-mono">Total Qty</span>
            <span className="ml-2 font-mono font-semibold text-slate-900">{stats.totalQty.toLocaleString("en-IN")}</span>
          </div>
          <div className="text-xs">
            <span className="text-gray-500 uppercase tracking-widest font-mono">Pending Qty</span>
            <span className="ml-2 font-mono font-semibold text-amber-700">{stats.pendingQty.toLocaleString("en-IN")}</span>
          </div>
          <div className="text-xs">
            <span className="text-gray-500 uppercase tracking-widest font-mono">Pending Value</span>
            <span className="ml-2 font-mono font-semibold text-slate-900">₹ {stats.pendingValue.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="p-12 text-center text-sm text-gray-500">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center" data-testid="consolidated-empty">
            <Layers className="mx-auto text-gray-300 mb-3" size={32} />
            <div className="font-display text-lg font-semibold text-slate-900">Nothing matches your filters</div>
            <div className="text-sm text-gray-500 mt-1">Clear filters or upload some POs.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[2000px] w-full border-collapse text-left" data-testid="consolidated-table">
              <thead>
                <tr className="bg-emerald-900">
                  {["SL. NO.", "PO Status", "PARTY NAME", "PO/DO", "PO NO", "PO Date", "DUE Date", "Part Name", "Material No.", "Qty", "Pending", "Line", "Rate", "Pending ₹", "Hose", "QTY", "Fitting A", "QTY", "Fitting B", "QTY", "Sleeve", "QTY", "Remark 1", "Remark 2"].map((h, i) => (
                    <th key={`h-${i}`} className={HDR}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(({ po, li, idx }) => {
                  const closed = li.line_status === "CLOSED";
                  const pendingValue = Number(li.pending_qty || 0) * Number(li.unit_price || 0);
                  return (
                    <tr key={`${po.id}-${idx}`} data-testid={`consolidated-row-${po.id}-${idx}`} className={`border-t border-slate-200 ${closed ? "bg-emerald-50/30" : "bg-white hover:bg-slate-50"}`}>
                      <td className={COL + " font-mono font-semibold text-slate-900 text-center"}>{li.sr_no || "—"}</td>
                      <td className={COL}>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_COLORS[po.status] || "bg-gray-50 text-gray-700 border-gray-200"}`}>{po.status}</span>
                      </td>
                      <td className={COL + " font-medium text-slate-900"}>{po.customer_name || "—"}</td>
                      <td className={COL + " text-center font-mono"}>{po.doc_type || "PO"}</td>
                      <td className={COL + " font-mono"}>
                        <Link to={`/orders/${po.id}`} className="text-slate-900 hover:underline">{po.po_number || "—"}</Link>
                      </td>
                      <td className={COL + " font-mono"}>{po.po_date || "—"}</td>
                      <td className={COL + " font-mono"}>{po.due_date || "—"}</td>
                      <td className={COL}>{li.part_name || "—"}</td>
                      <td className={COL + " font-mono"}>{li.material_no || li.part_code || "—"}</td>
                      <td className={COL + " text-right font-mono"}>{Number(li.quantity || 0).toLocaleString("en-IN")}</td>
                      <td className={COL + " text-right font-mono " + (closed ? "text-gray-400" : "text-amber-700 font-semibold")}>{Number(li.pending_qty ?? li.quantity ?? 0).toLocaleString("en-IN")}</td>
                      <td className={COL + " text-center"}>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${closed ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                          {li.line_status || "OPEN"}
                        </span>
                      </td>
                      <td className={COL + " text-right font-mono"}>{Number(li.unit_price || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      <td className={COL + " text-right font-mono font-semibold text-slate-900"}>{pendingValue.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      <td className={COL + " font-mono text-slate-700"}>{li.hose_spec || "—"}</td>
                      <td className={COL + " text-right font-mono"}>{Number(li.hose_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      <td className={COL}>{li.fitting_a || "—"}</td>
                      <td className={COL + " text-right font-mono"}>{Number(li.fitting_a_qty || 0).toLocaleString("en-IN")}</td>
                      <td className={COL}>{li.fitting_b || "—"}</td>
                      <td className={COL + " text-right font-mono"}>{Number(li.fitting_b_qty || 0).toLocaleString("en-IN")}</td>
                      <td className={COL}>{li.sleeve || "—"}</td>
                      <td className={COL + " text-right font-mono"}>{Number(li.sleeve_qty || 0).toLocaleString("en-IN")}</td>
                      <td className={COL + " text-xs text-gray-600 max-w-[160px] truncate"} title={li.remark_1 || ""}>{li.remark_1 || "—"}</td>
                      <td className={COL + " text-xs text-gray-600 max-w-[160px] truncate"} title={li.remark_2 || ""}>{li.remark_2 || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
